import CloseIcon from '@mui/icons-material/Close';
import Avatar from '@mui/material/Avatar';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { api, personImageUrl } from '../../api/client';
import type { ItemPerson } from '../../api/types';
import { useApi } from '../../api/useApi';
import AsyncSection from '../AsyncSection';

/// Who somebody is, fetched the moment they are opened and not before. The provider is asked
/// through our own server, so the browser never talks to it directly - the same reason the
/// portrait comes from us as well.
export default function PersonDialog({ person, onClose }: { person: ItemPerson; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  const providerKey = person.providerKey ?? '';
  const personId = person.personId ?? '';

  const details = useApi(
    ['person', providerKey, personId, language],
    () => api.person(providerKey, personId, language),
    { enabled: providerKey.length > 0 && personId.length > 0 },
  );

  const portrait = providerKey && personId ? personImageUrl(providerKey, personId, language) : undefined;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ position: 'relative' }}>
        <IconButton
          onClick={onClose}
          aria-label={t('common.close')}
          sx={{ position: 'absolute', insetInlineEnd: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ alignItems: 'flex-start' }}>
          <Avatar
            src={portrait}
            alt=""
            sx={{ width: 120, height: 120, fontSize: 40, flexShrink: 0 }}
          >
            {person.name.slice(0, 1)}
          </Avatar>

          <Stack spacing={1} sx={{ minWidth: 0 }}>
            <Typography variant="h3">{person.name}</Typography>

            {person.role && <Typography color="text.secondary">{person.role}</Typography>}

            <AsyncSection loading={details.loading} error={details.error} onRetry={details.reload}>
              <Stack spacing={1}>
                {details.data?.birthDate && (
                  <Typography variant="body2" color="text.secondary">
                    {t('person.born', {
                      date: new Date(details.data.birthDate).toLocaleDateString(language),
                      place: details.data.placeOfBirth ?? '',
                    }).trim()}
                  </Typography>
                )}

                {details.data?.deathDate && (
                  <Typography variant="body2" color="text.secondary">
                    {t('person.died', {
                      date: new Date(details.data.deathDate).toLocaleDateString(language),
                    })}
                  </Typography>
                )}

                <Typography sx={{ whiteSpace: 'pre-line' }}>
                  {details.data?.biography || t('person.noBiography')}
                </Typography>
              </Stack>
            </AsyncSection>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
